import { useCallback, useMemo } from "react";
import { JsonView, defaultStyles } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";

import type { RendererProps } from "../types";

function tryParseJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function deepParseJsonStrings(data: unknown): unknown {
  if (typeof data === "string") {
    const parsed = tryParseJson(data);
    return parsed !== data && typeof parsed === "object" && parsed !== null
      ? deepParseJsonStrings(parsed)
      : data;
  }

  if (Array.isArray(data)) {
    return data.map(deepParseJsonStrings);
  }

  if (typeof data === "object" && data !== null) {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [key, deepParseJsonStrings(value)]),
    );
  }

  return data;
}

interface JsonRendererProps extends RendererProps {
  renderLargeValueAsRaw?: boolean;
}

export function JsonRenderer({ content, renderLargeValueAsRaw = false }: JsonRendererProps) {
  const rawText = typeof content.rawValue === "string" ? content.rawValue : null;
  const shouldRenderRaw = renderLargeValueAsRaw && rawText !== null;
  const parsed = useMemo(() => {
    if (shouldRenderRaw) {
      return undefined;
    }

    const parsedValue = content.parsedValue ?? (rawText === null ? null : tryParseJson(rawText));
    return deepParseJsonStrings(parsedValue);
  }, [content.parsedValue, rawText, shouldRenderRaw]);
  const jsonData =
    Array.isArray(parsed) || (typeof parsed === "object" && parsed !== null)
      ? parsed
      : { value: parsed };
  const shouldExpandNode = useCallback((level: number) => level < 2, []);

  if (shouldRenderRaw) {
    return (
      <pre className="m-0 max-h-[60vh] w-full overflow-auto whitespace-pre-wrap break-words rounded-lg border border-border/40 bg-muted/20 p-4 font-mono text-[13px] leading-relaxed text-foreground">
        {rawText}
      </pre>
    );
  }

  return (
    <div className="json-view-wrapper overflow-x-auto rounded-lg border border-border/40 bg-muted/20 p-4 font-mono text-[13px] [&>div]:!bg-transparent [&>div]:!p-0 [&_*]:!font-normal">
      <JsonView
        data={jsonData}
        shouldExpandNode={shouldExpandNode}
        clickToExpandNode
        style={defaultStyles}
      />
    </div>
  );
}
